import { useState } from 'react';
import { ChevronDown, ChevronUp, Paperclip } from 'lucide-react';
import { formatInstant } from '@ampersandbrands/ui';
import { HtmlBody } from '@/components/HtmlBody';
import { usePublishedContent, type ContentItem } from '@/lib/useContent';
import { apiGet } from '@/lib/api';
import { API_BASE } from '@/lib/apiBase';
import { useMarkAreaSeen } from '@/lib/useHomeSummary';

function AttachmentButton({ itemId, att }: { itemId: number; att: { id: number; filename: string } }) {
  const [busy, setBusy] = useState(false);

  async function open() {
    setBusy(true);
    try {
      const { url } = await apiGet<{ url: string }>(`${API_BASE}/v1/content/published/${itemId}/attachments/${att.id}`);
      window.open(url, '_blank', 'noopener');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={open}
      disabled={busy}
      className="inline-flex items-center gap-1 text-xs text-candy-teal underline-offset-2 hover:underline disabled:opacity-50 dark:text-candy-teal-dark"
    >
      <Paperclip className="h-3 w-3" aria-hidden />
      {busy ? 'Opening…' : att.filename}
    </button>
  );
}

function ResourceCard({ item }: { item: ContentItem }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = !!item.body_html || item.attachments.length > 0;

  return (
    <div className="rounded-xl border border-candy-line bg-candy-white shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
      <button
        type="button"
        onClick={() => hasDetail && setExpanded((v) => !v)}
        disabled={!hasDetail}
        className="flex w-full items-center justify-between gap-3 p-4 text-left disabled:cursor-default"
        aria-expanded={hasDetail ? expanded : undefined}
      >
        <div className="min-w-0">
          <p className="truncate text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">{item.title}</p>
          <p className="mt-0.5 text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
            {formatInstant(item.updated_at, { dateOnly: true })}
          </p>
        </div>
        {hasDetail && (
          expanded
            ? <ChevronUp className="h-4 w-4 shrink-0 text-candy-ink-muted dark:text-candy-dark-ink-muted" aria-hidden />
            : <ChevronDown className="h-4 w-4 shrink-0 text-candy-ink-muted dark:text-candy-dark-ink-muted" aria-hidden />
        )}
      </button>

      {expanded && hasDetail && (
        <div className="border-t border-candy-line px-4 pb-4 pt-3 dark:border-candy-dark-line">
          {item.body_html && <HtmlBody src={item.body_html} />}
          {item.attachments.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {item.attachments.map((att) => (
                <AttachmentButton key={att.id} itemId={item.id} att={att} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Resources: the reference material HQ publishes for stores.
 *
 * The page is called Resources because that is what stores call it, but the
 * content area behind it is still named `training` in the database and in the
 * Cockpit publishing screen. Renaming the area key would orphan every item
 * already published, so the label changed and the key did not.
 */
export function Resources() {
  useMarkAreaSeen('training');
  const resourcesQ = usePublishedContent('training');

  const items = [...(resourcesQ.data ?? [])].sort((a, b) => {
    const at = a.published_at ? new Date(a.published_at).getTime() : 0;
    const bt = b.published_at ? new Date(b.published_at).getTime() : 0;
    return bt - at;
  });

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">Resources</h1>
        <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">Resources &amp; guides from HQ</p>
      </div>

      {resourcesQ.isLoading && (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl bg-candy-cream-warm dark:bg-candy-dark-surface-warm" />
          ))}
        </div>
      )}

      {!resourcesQ.isLoading && items.length === 0 && (
        <div className="rounded-xl border border-candy-line bg-candy-white px-4 py-6 text-center shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
          <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
            No resources yet. Check back soon.
          </p>
        </div>
      )}

      {!resourcesQ.isLoading && items.length > 0 && (
        <div className="space-y-4">
          {items.map((item) => (
            <ResourceCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
